import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line no-restricted-imports
import { Grid } from 'semantic-ui-react';

// Import utils
import { dateFormatter } from '../../../helpers/dates';
import { currencyFormatter } from '../../../helpers/utils';
import useRooms from '../../../hooks/useRooms';

import { ScheduleView, useScheduledDays, useTuitionAndFees } from '../../../enrollments';
import { EnrollmentStatusEnum } from '../../../enrollments/enums';
import { useStudentEnrollment } from '../../studentsHooks';
import EnrollmentInvite from './InvitationForm';

import { EnrollmentStatusDropdown } from './EnrollmentStatus';

const SHOW_STUDENT_ENROLLMENT_FEATURES = false;

export default function StudentEnrollment({ student }: { student: any }) {
  const { t } = useTranslation();
  const rooms = useRooms();
  const tuitionAndFees = useTuitionAndFees(student);
  const scheduledDays = useScheduledDays(student);
  const { enrollmentStatus, enrollmentDate, unenrollmentData } = useStudentEnrollment(student);

  const tuition = useMemo(() => {
    if (!tuitionAndFees?.tuition?.amount) return '--';
    return currencyFormatter(tuitionAndFees.tuition.amount / 100, { precision: 2 });
  }, [tuitionAndFees?.tuition?.amount]);

  const roomsLabel = useMemo(() => {
    if (!student?.rooms?.length) return '--';
    return rooms
      .filter((room: any) => student.rooms.includes(room.id))
      .map((room: any) => room.name)
      .join(', ');
  }, [student?.rooms, rooms]);

  if (!student?.id) return null;

  const COLUMN_1_WIDTH = 6;
  const COLUMN_2_WIDTH = 10;

  return (
    <>
      <Grid>
        <Grid.Row>
          <Grid.Column width={COLUMN_1_WIDTH}>{t('enrollments.fields.room')}</Grid.Column>
          <Grid.Column width={COLUMN_2_WIDTH}>
            <strong>{roomsLabel}</strong>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={COLUMN_1_WIDTH}>{t('enrollments.enrollmentInfoStartDateLabel')}</Grid.Column>
          <Grid.Column width={COLUMN_2_WIDTH}>
            <strong>
              {enrollmentDate ? dateFormatter(enrollmentDate) : t('enrollments.details.noEnrollmentDate')}
            </strong>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={COLUMN_1_WIDTH}>{t('common.schedule')}</Grid.Column>
          <Grid.Column width={COLUMN_2_WIDTH}>
            {!scheduledDays?.length && <strong>{t('students.noScheduleLabel')}</strong>}
            {!!scheduledDays?.length && (
              <ScheduleView hasTitle={false} scheduledDays={scheduledDays} className="enrollment" />
            )}
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={COLUMN_1_WIDTH}>{t('enrollments.enrollmentInfoTuitionLabel')}</Grid.Column>
          <Grid.Column width={COLUMN_2_WIDTH}>
            <strong>{tuition}</strong>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={COLUMN_1_WIDTH}>{t('enrollments.enrollmentInfoFeesLabel')}</Grid.Column>
          <Grid.Column width={COLUMN_2_WIDTH}>
            <strong>
              {!tuitionAndFees.fees?.length && <div>{'--'}</div>}
              {tuitionAndFees.fees?.map((fee, index) => {
                const feeAmount =
                  fee?.amount !== undefined ? currencyFormatter(fee.amount / 100, { precision: 2 }) : '--';
                return <div key={index}>{`${feeAmount} ${fee.name}`}</div>;
              })}
            </strong>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={COLUMN_1_WIDTH}>{t('enrollments.tuitionNotes')}</Grid.Column>
          <Grid.Column width={COLUMN_2_WIDTH}>
            <strong>
              <div>{tuitionAndFees.tuition?.notes ?? '--'}</div>
            </strong>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={COLUMN_1_WIDTH}>{t('enrollments.details.status')}</Grid.Column>
          <Grid.Column width={COLUMN_2_WIDTH}>
            <EnrollmentStatusDropdown enrollmentStatus={enrollmentStatus} enrollmentDate={enrollmentDate} />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      {enrollmentStatus === EnrollmentStatusEnum.ENROLLED && unenrollmentData?.date && (
        <Grid className="rounded-lg border-2 border-yellow-900 bg-yellow-50">
          <Grid.Row>
            <Grid.Column width={COLUMN_1_WIDTH}>{t('enrollments.unenrollmentDate')}</Grid.Column>
            <Grid.Column width={COLUMN_2_WIDTH}>
              <strong>
                {`${student?.firstName} ${t('enrollments.details.scheduledUnenrollment')} ${dateFormatter(
                  unenrollmentData?.date
                )}`}
              </strong>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={COLUMN_1_WIDTH}>{t('enrollments.unenrollmentInitiatedBy')}</Grid.Column>
            <Grid.Column width={COLUMN_2_WIDTH}>
              <strong>{unenrollmentData?.initiatedBy || '--'}</strong>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={COLUMN_1_WIDTH}>{t('enrollments.unenrollmentReason')}</Grid.Column>
            <Grid.Column width={COLUMN_2_WIDTH}>
              <strong>{unenrollmentData?.reason || '--'}</strong>
            </Grid.Column>
          </Grid.Row>
          {unenrollmentData?.settlementAdvisory && (
            <>
              {!unenrollmentData.settlementAdvisory.meetsNotice &&
                unenrollmentData.settlementAdvisory.prorateAmountCents > 0 && (
                  <Grid.Row>
                    <Grid.Column width={COLUMN_1_WIDTH}>{t('enrollments.shortNoticeFee')}</Grid.Column>
                    <Grid.Column width={COLUMN_2_WIDTH}>
                      <strong>
                        {currencyFormatter(unenrollmentData.settlementAdvisory.prorateAmountCents / 100, {
                          precision: 2,
                        })}
                      </strong>
                    </Grid.Column>
                  </Grid.Row>
                )}
              {unenrollmentData.settlementAdvisory.depositHeldCents > 0 && (
                <Grid.Row>
                  <Grid.Column width={COLUMN_1_WIDTH}>{t('enrollments.depositHeld')}</Grid.Column>
                  <Grid.Column width={COLUMN_2_WIDTH}>
                    <strong>
                      {currencyFormatter(unenrollmentData.settlementAdvisory.depositHeldCents / 100, {
                        precision: 2,
                      })}
                    </strong>
                  </Grid.Column>
                </Grid.Row>
              )}
              {unenrollmentData.settlementAdvisory.depositAppliedCents > 0 && (
                <Grid.Row>
                  <Grid.Column width={COLUMN_1_WIDTH}>
                    <span className="text-slate-500">{t('enrollments.depositAppliedToFee')}</span>
                  </Grid.Column>
                  <Grid.Column width={COLUMN_2_WIDTH}>
                    <span className="text-slate-500">
                      {`− ${currencyFormatter(unenrollmentData.settlementAdvisory.depositAppliedCents / 100, {
                        precision: 2,
                      })}`}
                    </span>
                  </Grid.Column>
                </Grid.Row>
              )}
              <Grid.Row className="border-t border-yellow-800/30">
                <Grid.Column width={COLUMN_1_WIDTH}>
                  <strong>{t('enrollments.netOwed')}</strong>
                </Grid.Column>
                <Grid.Column width={COLUMN_2_WIDTH}>
                  <strong
                    className={
                      unenrollmentData.settlementAdvisory.netOwedCents < 0
                        ? 'text-amber-700'
                        : unenrollmentData.settlementAdvisory.netOwedCents > 0
                          ? 'text-green-700'
                          : ''
                    }
                  >
                    {currencyFormatter(Math.abs(unenrollmentData.settlementAdvisory.netOwedCents) / 100, {
                      precision: 2,
                    })}
                    {unenrollmentData.settlementAdvisory.netOwedCents < 0
                      ? ` (${t('enrollments.owedToFamily')})`
                      : unenrollmentData.settlementAdvisory.netOwedCents > 0
                        ? ` (${t('enrollments.owedToSchool')})`
                        : null}
                  </strong>
                </Grid.Column>
              </Grid.Row>
            </>
          )}
        </Grid>
      )}
      {enrollmentStatus === EnrollmentStatusEnum.UNENROLLED && (
        <Grid className="rounded-lg border-2 border-red-900 bg-red-50">
          <Grid.Row>
            <Grid.Column width={COLUMN_1_WIDTH}>{t('enrollments.unenrollmentDate')}</Grid.Column>
            <Grid.Column width={COLUMN_2_WIDTH}>
              <strong>
                {unenrollmentData?.date ? dateFormatter(unenrollmentData.date) : t('enrollments.noUnenrollmentDate')}
              </strong>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={COLUMN_1_WIDTH}>{t('enrollments.unenrollmentInitiatedBy')}</Grid.Column>
            <Grid.Column width={COLUMN_2_WIDTH}>
              <strong>{unenrollmentData?.initiatedBy ?? '--'}</strong>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={COLUMN_1_WIDTH}>{t('enrollments.unenrollmentReason')}</Grid.Column>
            <Grid.Column width={COLUMN_2_WIDTH}>
              <strong>{unenrollmentData?.reason ?? '--'}</strong>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      )}
      {SHOW_STUDENT_ENROLLMENT_FEATURES && <EnrollmentInvite />}
    </>
  );
}
